import { AddressInput } from '@buy-viasat/types/build/bv';
import { Button, ISurfaceProps, Surface, Txt } from '@vst/beam';

import styles from './addressSummary.module.scss';

type AddressSummaryProps = Omit<ISurfaceProps, 'ref'> & {
  title: string;
  subtitle: string;
  buttonText: string;
  address: AddressInput;
  onAddressUpdate: () => void;
};

export const AddressSummary = (props: AddressSummaryProps) => {
  const { title, subtitle, buttonText, address, onAddressUpdate, ...surfaceProps } = props;
  const formattedAddressLine1 = address.addressLines[1]
    ? `${address.addressLines[0]} ${address.addressLines[1]}`
    : `${address.addressLines[0]}`;
  const formattedAddressLine2 = formatAddressLine2(address.municipality, address.region, address.postalCode);

  return (
    <Surface className={styles['wrapper']} {...surfaceProps}>
      <Surface className={styles['container']}>
        <Txt data-testid={'headlineServiceAddress'} color={'subtle'} variant="headerCapsSmall">
          {title}
        </Txt>

        <Surface data-testid={'addressText'} className={styles['addressText']} pt={'12px'}>
          <Txt variant="bodySmallRegular" color={'regular'} data-testid="addressLines">
            {formattedAddressLine1}
          </Txt>
          <Txt variant="bodySmallRegular" color={'regular'} pb={'12px'} data-testid="municipality">
            {formattedAddressLine2}
          </Txt>

          <Txt variant="smallRegular" component="span" color={'subtle'} data-testid="notAddress">
            {subtitle}
          </Txt>

          <Button
            data-testid={'updateButton'}
            variant={'tertiary'}
            onClick={onAddressUpdate}
            className={styles['button']}
            type={'button'}
            ml={'4px'}
            buttonSize={'small'}
          >
            {buttonText}
          </Button>
        </Surface>
      </Surface>
    </Surface>
  );
};

export const formatAddressLine2 = (
  municipality: AddressInput['municipality'],
  region: AddressInput['region'],
  postalCode: AddressInput['postalCode'],
): string => {
  const acc: string[] = [];

  [municipality, region].forEach((value) => {
    if (value) {
      acc.push(value);
    }
  });

  const result = acc.length > 0 ? acc.join(', ') : '';

  if (!postalCode) {
    return result;
  }

  if (!result) {
    return postalCode;
  }

  const separator = municipality && !region ? ', ' : ' ';
  return result.concat(separator + postalCode);
};
